import React from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import { graphql } from '@apollo/react-hoc';
import get from 'lodash/get';
import pick from 'lodash/pick';

import { defineMessages, FormattedMessage, injectIntl } from 'react-intl';
import { routerShape } from 'react-router';
import Header from '../../components/Header';
import { SimpleGraphQLComponent } from '../../utils/apollo';
import CollegeEventsFilterForm from '../../components/CollegeEventsFilterForm';
import CreateFilterMutation from '../CollegeEventsCreateFilter/graphql/createFilterMutation';
import collegeEventFilterFragment from '../CollegeEventsEditEvent/graphql/collegeEventFilterFragment';
import PageTitle from '../../components/PageTitle';
import Toast from '../../components/Toast';

import './styles.css';

const messages = defineMessages({
  header: {
    id: 'collegeevents.filters.duplicate.header',
    defaultMessage: 'Duplicate Filter',
  },
  errorUniqueName: {
    id: 'collegeevents.filters.duplicate.error.uniqueName',
    defaultMessage: 'Filter name should be unique.',
  },
  saveError: {
    id: 'collegeevents.filters.duplicate.save-error',
    defaultMessage: 'There was an error while trying to save your changes.',
  },
  successTitle: {
    id: 'collegeevents.filters.create.success-title',
    defaultMessage: 'Filter created',
  },
  successDescription: {
    id: 'collegeevents.filters.create.success-description',
    defaultMessage: 'Your new filter was successfully created!',
  },
});

class CollegeEventsDuplicateFilterPage extends React.PureComponent {
  static propTypes = {
    viewer: PropTypes.object,
    node: PropTypes.object,
    intl: PropTypes.shape({
      formatMessage: PropTypes.func.isRequired,
    }).isRequired,
    createFilter: PropTypes.func.isRequired,
    router: routerShape,
  };

  constructor(props) {
    super(props);
    const { node } = props;
    const filter = pick(node,
      ['name', 'genders', 'distanceUnit', 'distanceValue', 'postalCode', 'ethnicities', 'gradeLevels', 'gpaScores']);
    this.state = {
      saving: false,
      saveError: false,
      saveSuccess: false,
      filter,
    };
  }

  componentWillReceiveProps(nextProps) {
    if (this.state.saving) return;
    const { node } = nextProps;
    const filter = pick(node,
      ['name', 'genders', 'distanceUnit', 'distanceValue', 'postalCode', 'ethnicities', 'gradeLevels', 'gpaScores']);
    this.setState({ filter });
  }

  onSave = (filter) => {
    const { createFilter } = this.props;
    this.setState({ saving: true, saveSuccess: false, filter });
    const variables = {
      input: {
        collegeEventFilter: filter,
      },
    };
    createFilter({ variables }).then(this.onSaveSuccess, this.onSaveError);
  }

  onCancel = () => {
    this.props.router.push('/college-events/view-filters');
  }

  onSaveError = (err) => {
    const errorMessage = get(err, 'graphQLErrors[0].message') || get(err, 'message') || '';
    const saveError = errorMessage.includes('name should be unique') ? messages.errorUniqueName : messages.saveError;
    this.setState({ saveSuccess: false, saveError, saving: false });
  }

  onSaveSuccess = () => {
    this.setState({ saveSuccess: true, saveError: false, saving: false });
    this.props.router.push('/college-events/view-filters');
  }

  onDeactivate = () => {
    this.setState({ saveSuccess: false });
  }

  render() {
    const { saveSuccess, saveError, saving, filter } = this.state;
    const { intl } = this.props;
    filter.visible = true;

    return (
      <div>
        <PageTitle subTitle="Duplicate Filter" title="Events" category="Intersect" />
        <Header styleName="college-filters-edit-form-header" as="h1"><FormattedMessage {...messages.header} /></Header>
        <Toast
          active={saveSuccess}
          onDeactivate={this.onDeactivate}
          title={intl.formatMessage(messages.successTitle)}
          description={intl.formatMessage(messages.successDescription)}
        />
        <CollegeEventsFilterForm
          filter={filter}
          onSave={this.onSave}
          onCancel={this.onCancel}
          saveError={saveError}
          saving={saving}
        />
      </div>
    );
  }
}

export const CollegeEventsDuplicateFilterPageComponent = injectIntl(CollegeEventsDuplicateFilterPage);

const query = gql`
  query collegeEventDuplicateFilterPage($id: ID!) {
    viewer {
      id
    }
    node(id: $id) {
      ...CollegeEventFilter
    }
  }
  ${collegeEventFilterFragment}
`;

const graphqlData = graphql(query,
  {
    options: ({ params }) => ({
      variables: {
        id: params.id,
      },
    }),
  });

const createCollegeEventFilter = graphql(CreateFilterMutation, {
  name: 'createFilter',
  options: {
    refetchQueries: ['collegeEventsFilters', 'collegeEventFilters'],
    awaitRefetchQueries: true,
  },
});

export default SimpleGraphQLComponent(
  graphqlData,
  createCollegeEventFilter,
)(CollegeEventsDuplicateFilterPageComponent);
